import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { LayoutDashboard, Users, CalendarCheck, CalendarDays, Wallet, FileText, Target, Megaphone, Calendar, Menu, LogOut, ChevronRight, Search, Lock, FileSignature, BookOpen, Network, Shield, UserCircle, MapPin, Mail, KeyRound, Fingerprint, Bell, WifiOff, ClipboardList, Package, BarChart3, Languages } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getUser, clearAuth, isAdmin, isHR } from '@/lib/auth';
import api from '@/lib/api';
import NotificationBell from '@/components/NotificationBell';
import MobileBottomNav from '@/components/MobileBottomNav';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';

const navItems = [
  { labelKey: 'nav.dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { labelKey: 'nav.clockInOut', icon: Fingerprint, path: '/dashboard/clock' },
  { labelKey: 'nav.employees', icon: Users, path: '/dashboard/employees', adminOnly: true },
  { labelKey: 'nav.attendance', icon: CalendarCheck, path: '/dashboard/attendance' },
  { labelKey: 'nav.leave', icon: CalendarDays, path: '/dashboard/leave' },
  { labelKey: 'nav.payroll', icon: Wallet, path: '/dashboard/payroll' },
  { labelKey: 'nav.performance', icon: Target, path: '/dashboard/performance' },
  { labelKey: 'nav.documents', icon: FileText, path: '/dashboard/documents' },
  { labelKey: 'nav.announcements', icon: Megaphone, path: '/dashboard/announcements' },
  { labelKey: 'nav.holidays', icon: Calendar, path: '/dashboard/holidays' },
  { labelKey: 'nav.hrLetters', icon: FileSignature, path: '/dashboard/hr-letters', adminOnly: true },
  { labelKey: 'nav.hrPolicy', icon: BookOpen, path: '/dashboard/hr-policy' },
  { labelKey: 'nav.journeyPlans', icon: MapPin, path: '/dashboard/journey-plans' },
  { labelKey: 'nav.teamMaster', icon: Network, path: '/dashboard/team-master', adminOnly: true },
  { labelKey: 'nav.auditLogs', icon: Shield, path: '/dashboard/audit-logs', adminOnly: true },
  { labelKey: 'nav.emailTemplates', icon: Mail, path: '/dashboard/email-templates', adminOnly: true },
  { labelKey: 'nav.userManagement', icon: KeyRound, path: '/dashboard/user-management', adminOnly: true },
  { labelKey: 'nav.notifications', icon: Bell, path: '/dashboard/notifications' },
  { labelKey: 'nav.onboarding', icon: ClipboardList, path: '/dashboard/onboarding' },
  { labelKey: 'nav.assets', icon: Package, path: '/dashboard/assets' },
  { labelKey: 'nav.reports', icon: BarChart3, path: '/dashboard/reports', adminOnly: true },
  { labelKey: 'nav.myProfile', icon: UserCircle, path: '/dashboard/my-profile' },
  { labelKey: 'nav.exitForm', icon: LogOut, path: '/dashboard/exit-form' },
];

function SidebarContent({ onNavClick }) {
  const user = getUser();
  const { t } = useTranslation();
  return (
    <div className="flex flex-col h-full" data-testid="sidebar">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
        <img
          src="/prismoline-logo.png"
          alt="Prismoline"
          className="h-8 brightness-0 invert"
          data-testid="sidebar-logo"
        />
      </div>
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.filter(item => !item.adminOnly || isAdmin() || isHR()).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/dashboard'}
              onClick={onNavClick}
              data-testid={`sidebar-nav-${item.labelKey.split('.')[1].toLowerCase()}-link`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-[background-color,color] duration-150 ${
                  isActive
                    ? 'bg-white/10 text-white border-l-[3px] border-l-[#F5CB08] -ml-px'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px] shrink-0" />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
      </ScrollArea>
      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8 bg-[#F5CB08]">
            <AvatarFallback className="bg-[#F5CB08] text-[#0D2850] text-xs font-bold">
              {user?.name?.charAt(0) || 'A'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
            <p className="text-xs text-white/50 truncate capitalize">{user?.role || 'admin'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', new: '', confirm: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const navigate = useNavigate();
  const user = getUser();
  const { t, i18n } = useTranslation();
  usePushNotifications();
  const isOffline = useOfflineStatus();

  const toggleLang = () => {
    const next = i18n.language === 'en' ? 'hi' : 'en';
    i18n.changeLanguage(next);
    localStorage.setItem('prismoline_lang', next);
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    if (!pwdForm.current || !pwdForm.new) { toast.error('Fill all fields'); return; }
    if (pwdForm.new !== pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwdForm.new.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPwdLoading(true);
    try {
      await api.post('/api/auth/change-password', { current_password: pwdForm.current, new_password: pwdForm.new });
      toast.success('Password changed successfully');
      setPwdOpen(false);
      setPwdForm({ current: '', new: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setPwdLoading(false);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F7FAFF]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-[260px] flex-col bg-[#0D2850] shrink-0" style={{background: 'linear-gradient(180deg, #0D2850 0%, #0B2246 100%)'}}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[260px] bg-[#0D2850] border-0">
          <SidebarContent onNavClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 bg-white/85 backdrop-blur-md border-b border-slate-200 flex items-center px-4 lg:px-6 gap-4 shrink-0 z-10" data-testid="topbar">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
          </Sheet>

          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={t('common.search')}
                className="pl-9 h-9 bg-slate-50 border-slate-200 text-sm"
                data-testid="topbar-global-search-input"
              />
            </div>
          </div>

          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 h-9 px-2" data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[#0D2850] text-white text-xs font-bold">
                    {user?.name?.charAt(0) || 'A'}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm font-medium text-slate-700">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-xs text-slate-500">{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleLang} data-testid="language-toggle-btn">
                <Languages className="w-4 h-4 mr-2" /> {i18n.language === 'en' ? 'हिन्दी' : 'English'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="change-password-btn">
                <Lock className="w-4 h-4 mr-2" /> {t('common.changePassword')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          {isOffline && (
            <div className="bg-amber-500 text-white text-center py-2 px-4 flex items-center justify-center gap-2 text-sm font-medium" data-testid="offline-banner">
              <WifiOff className="w-4 h-4" /> You're offline — showing cached data
            </div>
          )}
          <div className="max-w-[1440px] px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-20 lg:pb-8">
            <Outlet />
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNav />
      </div>

      {/* Change Password Dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Current Password</Label><Input type="password" value={pwdForm.current} onChange={e => setPwdForm({...pwdForm, current: e.target.value})} data-testid="current-password-input" /></div>
            <div><Label>New Password</Label><Input type="password" value={pwdForm.new} onChange={e => setPwdForm({...pwdForm, new: e.target.value})} data-testid="new-password-input" /></div>
            <div><Label>Confirm Password</Label><Input type="password" value={pwdForm.confirm} onChange={e => setPwdForm({...pwdForm, confirm: e.target.value})} data-testid="confirm-password-input" /></div>
            <Button onClick={handleChangePassword} disabled={pwdLoading} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="change-password-submit">
              {pwdLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
